/**
 * Console pattern: responder/Publish — 平台发布.
 *
 * Spec doesn't have a dedicated section; this page is the "review pipeline"
 * companion to §5.4 Hotline 管理. It's the responder counterpart to the
 * caller-side Approvals: a board showing draft → submitted → official
 * progression for the user's local hotlines, and the action surface for
 * submitting / withdrawing / replying to platform reviewer comments.
 *
 * 静态原型：4 列看板 + 选中卡片的 review timeline。
 */

import { useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Hammer,
  MessageSquare,
  Send,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

import {
  ConsolePageHeader,
  ConsoleCard,
  ConsoleButton,
} from "../shells/console-shell";
import { cn } from "@/app/components/ui/utils";

type ReviewStage = "draft" | "submitted" | "needs_revision" | "official";

const STAGES: { key: ReviewStage; label: string; tone: string; tip: string }[] = [
  { key: "draft", label: "草稿", tone: "var(--brand-muted)", tip: "本地编辑中，未提交。" },
  { key: "submitted", label: "审核中", tone: "var(--brand-blue)", tip: "已提交平台，平均 2 个工作日内回复。" },
  { key: "needs_revision", label: "需修改", tone: "var(--c-status-warn-fg)", tip: "平台审核员已留下修改意见，按意见调整后重提。" },
  { key: "official", label: "已上架", tone: "var(--c-status-success-fg)", tip: "出现在公开 Marketplace，可被任意 Caller 调用。" },
];

type PipelineItem = {
  hotlineId: string;
  displayName: string;
  stage: ReviewStage;
  updatedAt: string;
  reviewer?: string;
  noteCount?: number;
};

const ITEMS: PipelineItem[] = [
  { hotlineId: "local.workspace-summary.v1", displayName: "工作区文档摘要", stage: "official", updatedAt: "3 天前", reviewer: "marketplace-team" },
  { hotlineId: "ops.notify@v1", displayName: "Slack 通知 · #ops 频道", stage: "draft", updatedAt: "刚刚" },
  { hotlineId: "calendar.create-event", displayName: "Google 日历 · 创建事件", stage: "needs_revision", updatedAt: "昨天 18:42", reviewer: "marketplace-team", noteCount: 3 },
  { hotlineId: "db.query.v0", displayName: "SQL 只读查询", stage: "submitted", updatedAt: "1 天前", reviewer: "queue · #4" },
];

export function ConsolePagePublish() {
  const [selectedId, setSelectedId] = useState<string | null>("calendar.create-event");
  const selected = ITEMS.find((i) => i.hotlineId === selectedId) ?? null;

  return (
    <>
      <ConsolePageHeader
        kicker="RESPONDER · 平台发布"
        title="把本机的 Hotline 提交到 Marketplace"
        description="只有 official 状态的 Hotline 会被外部 Caller 看到。Draft 永远在本地，提交是显式动作。"
        actions={
          <Link to="/console/hotlines">
            <ConsoleButton variant="ghost">
              <Hammer className="h-3.5 w-3.5" />
              管理 Hotline
            </ConsoleButton>
          </Link>
        }
      />

      {/* 4-stage Kanban */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {STAGES.map((s) => {
          const cards = ITEMS.filter((i) => i.stage === s.key);
          return (
            <div
              key={s.key}
              className="bg-white border flex flex-col"
              style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius)" }}
            >
              <div
                className="px-3 py-2 border-b flex items-center justify-between"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: s.tone }}
                  />
                  <span className="text-[12px] font-semibold truncate">{s.label}</span>
                </div>
                <span className="text-[10.5px] text-[var(--brand-muted)] tabular-nums">
                  {cards.length}
                </span>
              </div>
              <div className="p-2 flex flex-col gap-2 min-h-[120px]">
                {cards.length === 0 && (
                  <p className="text-[11px] text-[var(--brand-muted)] text-center py-4 leading-snug">
                    {s.tip}
                  </p>
                )}
                {cards.map((c) => {
                  const active = selectedId === c.hotlineId;
                  return (
                    <button
                      key={c.hotlineId}
                      onClick={() => setSelectedId(c.hotlineId)}
                      className={cn(
                        "text-left p-2 border bg-white text-[11.5px]",
                        active
                          ? "c-ink-border-2 c-shadow-1"
                          : "border-[var(--border)] hover:border-[var(--brand-muted)]",
                      )}
                      style={{ borderRadius: "var(--c-radius-sm)" }}
                    >
                      <p className="font-semibold text-[var(--ink)] truncate">{c.displayName}</p>
                      <p className="text-[10.5px] font-mono text-[var(--brand-muted)] truncate mt-0.5">
                        {c.hotlineId}
                      </p>
                      <div className="mt-1.5 flex items-center justify-between text-[10.5px] text-[var(--brand-muted)]">
                        <span>{c.updatedAt}</span>
                        {c.noteCount !== undefined && (
                          <span className="inline-flex items-center gap-0.5 text-[var(--c-status-warn-fg)] font-semibold">
                            <MessageSquare className="h-2.5 w-2.5" strokeWidth={2.5} />
                            {c.noteCount}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail */}
      {selected && <ReviewDetail item={selected} />}
    </>
  );
}

function ReviewDetail({ item }: { item: PipelineItem }) {
  return (
    <ConsoleCard padded={false}>
      <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[14px] font-semibold truncate">{item.displayName}</p>
            <StageChip stage={item.stage} />
          </div>
          <p className="text-[11.5px] font-mono text-[var(--brand-muted)] mt-0.5 truncate">
            {item.hotlineId}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {item.stage === "draft" && (
            <ConsoleButton variant="primary">
              <Send className="h-3.5 w-3.5" />
              提交审核
            </ConsoleButton>
          )}
          {item.stage === "needs_revision" && (
            <>
              <ConsoleButton variant="ghost">查看修改意见</ConsoleButton>
              <ConsoleButton variant="primary">
                <Send className="h-3.5 w-3.5" />
                修改后重提
              </ConsoleButton>
            </>
          )}
          {item.stage === "submitted" && (
            <ConsoleButton variant="ghost">撤回提交</ConsoleButton>
          )}
          {item.stage === "official" && (
            <ConsoleButton variant="ghost">在 Marketplace 查看</ConsoleButton>
          )}
        </div>
      </div>

      <div className="grid grid-cols-[1fr_280px] divide-x" style={{ borderColor: "var(--border)" }}>
        {/* Timeline */}
        <div className="p-5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--brand-muted)] mb-3">
            评审时间线
          </p>
          <ul className="space-y-3">
            {timelineFor(item).map((evt, i) => (
              <li key={i} className="flex items-start gap-3">
                <div
                  className={cn(
                    "h-5 w-5 shrink-0 flex items-center justify-center border bg-white",
                    evt.tone === "ok" && "border-[var(--c-status-success-fg)] text-[var(--c-status-success-fg)]",
                    evt.tone === "warn" && "border-[var(--c-status-warn-fg)] text-[var(--c-status-warn-fg)]",
                    evt.tone === "info" && "border-[var(--brand-muted)] text-[var(--brand-muted)]",
                    evt.tone === "err" && "border-[var(--c-status-error-fg)] text-[var(--c-status-error-fg)]",
                  )}
                  style={{ borderRadius: "var(--c-radius-pill)" }}
                >
                  {evt.tone === "ok" && <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />}
                  {evt.tone === "warn" && <MessageSquare className="h-3 w-3" strokeWidth={2.5} />}
                  {evt.tone === "info" && <Clock className="h-3 w-3" strokeWidth={2.5} />}
                  {evt.tone === "err" && <XCircle className="h-3 w-3" strokeWidth={2.5} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] text-[var(--ink)]">
                    <strong>{evt.title}</strong>
                    {evt.actor && <span className="text-[var(--brand-muted)] font-normal"> · {evt.actor}</span>}
                  </p>
                  {evt.body && (
                    <p className="text-[11.5px] text-[var(--ink)]/75 mt-0.5 leading-snug">
                      {evt.body}
                    </p>
                  )}
                  <p className="text-[10.5px] text-[var(--brand-muted)] mt-0.5">{evt.at}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Side: checks */}
        <div className="p-5 bg-[var(--brand-secondary)]/30">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--brand-muted)] mb-3">
            自动检查
          </p>
          <ul className="space-y-2 text-[12px]">
            <CheckRow ok label="display_name 不超过 40 字符" />
            <CheckRow ok label="capabilities 已声明 scope" />
            <CheckRow ok label="input/output schema 完整" />
            <CheckRow
              ok={item.stage !== "needs_revision"}
              label="示例可成功试拨"
              hint={item.stage === "needs_revision" ? "上次平台试拨返回 401，请确认 endpoint 鉴权方式" : undefined}
            />
            <CheckRow ok label="文档 description 大于 50 字" />
          </ul>

          <Link
            to="/console/help#publishing"
            className="mt-4 text-[11.5px] font-medium text-[var(--brand-muted)] hover:text-[var(--ink)] inline-flex items-center gap-1"
          >
            发布要求详解
            <ChevronRight className="h-3 w-3" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </ConsoleCard>
  );
}

function StageChip({ stage }: { stage: ReviewStage }) {
  const cls: Record<ReviewStage, string> = {
    draft: "c-status-warn",
    submitted: "c-status-info",
    needs_revision: "c-status-warn",
    official: "c-status-success",
  };
  const label: Record<ReviewStage, string> = {
    draft: "草稿",
    submitted: "审核中",
    needs_revision: "需修改",
    official: "已上架",
  };
  return <span className={cn("c-status text-[10px]", cls[stage])}>{label[stage]}</span>;
}

type Evt = {
  title: string;
  actor?: string;
  body?: string;
  at: string;
  tone: "ok" | "warn" | "info" | "err";
};

function timelineFor(item: PipelineItem): Evt[] {
  if (item.stage === "official") {
    return [
      { title: "已上架到 Marketplace", actor: "marketplace-team", at: "3 天前 14:02", tone: "ok" },
      { title: "通过审核", actor: "marketplace-team", at: "3 天前 13:45", tone: "ok" },
      { title: "提交审核", actor: "你", at: "5 天前 09:11", tone: "info" },
      { title: "草稿创建", actor: "你", at: "5 天前 08:20", tone: "info" },
    ];
  }
  if (item.stage === "needs_revision") {
    return [
      {
        title: "需修改",
        actor: "marketplace-team",
        body:
          "1) endpoint 必须支持 OAuth2，目前仅支持 API key；2) capabilities 缺 scope:calendar.events.write 的明确声明；3) 示例输入与 input_schema 类型不匹配。",
        at: "昨天 18:42",
        tone: "warn",
      },
      { title: "进入审核队列 #2", actor: "marketplace-team", at: "2 天前 11:00", tone: "info" },
      { title: "提交审核", actor: "你", at: "2 天前 10:55", tone: "info" },
      { title: "草稿创建", actor: "你", at: "1 周前", tone: "info" },
    ];
  }
  if (item.stage === "submitted") {
    return [
      { title: "排队中 · 队列位置 #4", actor: "marketplace-team", at: "1 天前", tone: "info" },
      { title: "提交审核", actor: "你", at: "1 天前", tone: "info" },
      { title: "草稿创建", actor: "你", at: "3 天前", tone: "info" },
    ];
  }
  return [
    { title: "本地编辑中", actor: "你", body: "尚未提交。提交前会自动跑发布检查。", at: "刚刚", tone: "info" },
    { title: "草稿创建", actor: "你", at: "5 分钟前", tone: "info" },
  ];
}

function CheckRow({ ok, label, hint }: { ok: boolean; label: string; hint?: string }) {
  return (
    <li className="flex items-start gap-2">
      {ok ? (
        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-[var(--c-status-success-fg)] shrink-0" strokeWidth={2.5} />
      ) : (
        <XCircle className="h-3.5 w-3.5 mt-0.5 text-[var(--c-status-error-fg)] shrink-0" strokeWidth={2.5} />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-[var(--ink)]/85">{label}</p>
        {hint && (
          <p className="text-[10.5px] text-[var(--c-status-warn-fg)] leading-snug mt-0.5">{hint}</p>
        )}
      </div>
    </li>
  );
}
